/**
 * useAISession Hook
 *
 * Manages AI session state for presentation generation and refinement.
 */

import { useState, useCallback, useEffect } from 'react';
import { PresentationContent } from '../../../ai/schemas/ContentBlock';
import { aiService, AIGenerationOptions } from '../services/AIService';

/**
 * Chat message in the conversation
 */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

/**
 * AI session state
 */
export interface AISessionState {
  sessionId: string | null;
  presentation: PresentationContent | null;
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
}

/**
 * useAISession return type
 */
export interface UseAISessionReturn extends AISessionState {
  generate: (prompt: string, options?: AIGenerationOptions) => Promise<boolean>;
  generateFromFile: (file: File, options?: AIGenerationOptions, prompt?: string) => Promise<boolean>;
  refine: (message: string, screenIndex?: number) => Promise<boolean>;
  updatePresentation: (updater: (prev: PresentationContent) => PresentationContent) => void;
  setPresentation: (presentation: PresentationContent | null) => void;
  setSessionId: (sessionId: string | null) => void;
  reset: () => void;
  clearError: () => void;
}

/**
 * Generate a unique message ID
 */
function generateMessageId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * useAISession hook
 */
export function useAISession(initialSessionId?: string): UseAISessionReturn {
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId || null);
  const [presentation, setPresentation] = useState<PresentationContent | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing session if sessionId is provided
  useEffect(() => {
    if (initialSessionId && !presentation) {
      loadSession(initialSessionId);
    }
  }, [initialSessionId]);

  /**
   * Load an existing session
   */
  const loadSession = async (id: string) => {
    setIsLoading(true);
    setError(null);

    const result = await aiService.getHistory(id);

    if (result.success && result.presentation) {
      setSessionId(id);
      setPresentation(result.presentation);

      // Convert history to messages
      if (result.history) {
        const loadedMessages: ChatMessage[] = result.history.map((item, index) => ({
          id: generateMessageId(),
          role: item.role as 'user' | 'assistant',
          content: item.content,
          timestamp: new Date(),
        }));
        setMessages(loadedMessages);
      }
    } else {
      setError(result.error || 'Failed to load session');
    }

    setIsLoading(false);
  };

  /**
   * Generate a new presentation
   */
  const generate = useCallback(
    async (prompt: string, options?: AIGenerationOptions): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      // Add user message
      const userMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: prompt,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      const result = await aiService.generate(prompt, options);

      if (result.success && result.presentation && result.sessionId) {
        setSessionId(result.sessionId);
        setPresentation(result.presentation);

        // Add assistant message
        const assistantMessage: ChatMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: `Created a ${result.presentation.screens.length}-screen presentation: "${result.presentation.title}"`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        setIsLoading(false);
        return true;
      } else {
        setError(result.error || 'Failed to generate presentation');
        setIsLoading(false);
        return false;
      }
    },
    []
  );

  /**
   * Generate a presentation from an uploaded file
   */
  const generateFromFile = useCallback(
    async (file: File, options?: AIGenerationOptions, prompt?: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      const userMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: prompt
          ? `Uploaded ${file.name} with instructions: ${prompt}`
          : `Uploaded ${file.name} for presentation generation`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      const result = await aiService.upload(file, options || {}, prompt);

      if (result.success && result.presentation && result.sessionId) {
        setSessionId(result.sessionId);
        setPresentation(result.presentation);

        const assistantMessage: ChatMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: `Created a ${result.presentation.screens.length}-screen presentation from ${file.name}: "${result.presentation.title}"`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        setIsLoading(false);
        return true;
      } else {
        setError(result.error || 'Failed to generate presentation from file');
        setIsLoading(false);
        return false;
      }
    },
    []
  );

  /**
   * Refine the current presentation
   */
  const refine = useCallback(
    async (message: string, screenIndex?: number): Promise<boolean> => {
      if (!sessionId) {
        setError('No active session. Generate a presentation first.');
        return false;
      }

      setIsLoading(true);
      setError(null);

      // Add user message
      const userMessage: ChatMessage = {
        id: generateMessageId(),
        role: 'user',
        content: message,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      const result = await aiService.refine(sessionId, message, screenIndex);

      if (result.success && result.presentation) {
        setPresentation(result.presentation);

        // Add assistant message
        const assistantMessage: ChatMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: 'Updated the presentation based on your feedback.',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        setIsLoading(false);
        return true;
      } else {
        setError(result.error || 'Failed to refine presentation');
        setIsLoading(false);
        return false;
      }
    },
    [sessionId]
  );

  /**
   * Reset the session
   */
  const reset = useCallback(() => {
    setSessionId(null);
    setPresentation(null);
    setMessages([]);
    setError(null);
    setIsLoading(false);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Update presentation state (e.g. to resolve image placeholders)
   */
  const updatePresentation = useCallback(
    (updater: (prev: PresentationContent) => PresentationContent) => {
      setPresentation((prev) => (prev ? updater(prev) : prev));
    },
    []
  );

  return {
    sessionId,
    presentation,
    messages,
    isLoading,
    error,
    generate,
    generateFromFile,
    refine,
    updatePresentation,
    setPresentation,
    setSessionId,
    reset,
    clearError,
  };
}
